import { supabase } from '@/lib/supabase';

type AssignmentRow = {
  class_id: string;
  created_at: string;
  id: string;
  subject_id: string;
  teacher_id: string;
};

export type AssignmentListItem = AssignmentRow & {
  class_label: string;
  subject_label: string;
  teacher_label: string;
};

export function mapAssignmentsForSchool(input: {
  assignments: AssignmentRow[];
  classes: Array<{ id: string; name: string; section: string | null }>;
  subjects: Array<{ code: string | null; id: string; name: string }>;
  teachers: Array<{ full_name: string | null; id: string }>;
}) {
  const classesMap = new Map(
    input.classes.map((item) => [
      item.id,
      item.section?.trim() ? `${item.name} - ${item.section}` : item.name,
    ] as const)
  );
  const subjectsMap = new Map(
    input.subjects.map((item) => [
      item.id,
      item.code?.trim() ? `${item.name} (${item.code})` : item.name,
    ] as const)
  );
  const teachersMap = new Map(
    input.teachers.map((item) => [item.id, item.full_name || 'Teacher'] as const)
  );

  return input.assignments.map((item) => ({
    ...item,
    class_label: classesMap.get(item.class_id) ?? 'Class unavailable',
    subject_label: subjectsMap.get(item.subject_id) ?? 'Subject unavailable',
    teacher_label: teachersMap.get(item.teacher_id) ?? 'Teacher unavailable',
  })) as AssignmentListItem[];
}

export async function fetchAssignmentRowsForSchool(schoolId: string) {
  const { data, error } = await supabase
    .from('class_subjects')
    .select('id, class_id, subject_id, teacher_id, created_at')
    .eq('school_id', schoolId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data ?? []) as AssignmentRow[];
}

export async function fetchAssignmentsForSchool(schoolId: string) {
  const [
    assignments,
    classes,
    subjects,
    teachers,
  ] = await Promise.all([
    fetchAssignmentRowsForSchool(schoolId),
    supabase.from('classes').select('id, name, section').eq('school_id', schoolId),
    supabase.from('subjects').select('id, name, code').eq('school_id', schoolId),
    supabase
      .from('profiles')
      .select('id, full_name')
      .eq('school_id', schoolId)
      .eq('role', 'teacher'),
  ]);

  if (classes.error) throw classes.error;
  if (subjects.error) throw subjects.error;
  if (teachers.error) throw teachers.error;

  return mapAssignmentsForSchool({
    assignments,
    classes: classes.data ?? [],
    subjects: subjects.data ?? [],
    teachers: teachers.data ?? [],
  });
}

export async function fetchAssignmentsForTeacher(teacherId: string) {
  const { data: assignments, error: assignmentsError } = await supabase
    .from('class_subjects')
    .select(`
      id,
      class_id,
      subject_id,
      created_at,
      classes (id, name, section),
      subjects (id, name, code)
    `)
    .eq('teacher_id', teacherId)
    .order('created_at', { ascending: false });

  if (assignmentsError) throw assignmentsError;

  return (assignments ?? []).map((item: any) => ({
    id: item.id,
    class_id: item.class_id,
    subject_id: item.subject_id,
    created_at: item.created_at,
    class_label: item.classes?.section?.trim() 
      ? `${item.classes.name} - ${item.classes.section}` 
      : item.classes?.name || 'Class unavailable',
    subject_label: item.subjects?.code?.trim()
      ? `${item.subjects.name} (${item.subjects.code})`
      : item.subjects?.name || 'Subject unavailable',
    teacher_label: '', // Not needed for teacher's own view
  })) as AssignmentListItem[];
}

export async function syncAssignmentContentOwnershipForSchool(schoolId: string) {
  void schoolId;
}

export async function createAssignment(input: {
  classId: string;
  schoolId: string;
  subjectId: string;
  teacherId: string;
}) {
  const { error } = await supabase.from('class_subjects').insert({
    class_id: input.classId,
    school_id: input.schoolId,
    subject_id: input.subjectId,
    teacher_id: input.teacherId,
  });

  if (error) throw error;
}

export async function updateAssignmentTeacher(input: {
  assignmentId: string;
  schoolId: string;
  teacherId: string;
}) {
  const { error } = await supabase
    .from('class_subjects')
    .update({
      teacher_id: input.teacherId,
    })
    .eq('id', input.assignmentId)
    .eq('school_id', input.schoolId);

  if (error) throw error;
}

export async function deleteAssignment(input: { assignmentId: string; schoolId: string }) {
  const { error } = await supabase
    .from('class_subjects')
    .delete()
    .eq('id', input.assignmentId)
    .eq('school_id', input.schoolId);

  if (error) throw error;
}
